/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CheckCircle, Plus, Trash2 } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { SettingsSection } from '../components/settings-section'
import { useUpdateOption } from '../hooks/use-update-option'

interface ReferralTier {
  min_volume: number
  rate: number
}

function parseTiers(raw: string): ReferralTier[] {
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (tier) =>
          typeof tier?.min_volume === 'number' &&
          typeof tier?.rate === 'number'
      )
      .map((tier) => ({
        min_volume: Number(tier.min_volume),
        rate: Number(tier.rate),
      }))
      .sort((a, b) => a.min_volume - b.min_volume)
  } catch {
    return []
  }
}

export function ReferralSettingsSection({
  defaultValues,
}: {
  defaultValues: {
    enabled: boolean
    autoGrant: boolean
    tiers: string
    firstTopupBonusRate: number
  }
}) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()

  const [enabled, setEnabled] = useState(defaultValues.enabled)
  const [autoGrant, setAutoGrant] = useState(defaultValues.autoGrant)
  const [tiers, setTiers] = useState<ReferralTier[]>(
    parseTiers(defaultValues.tiers)
  )
  const [firstTopupBonusRate, setFirstTopupBonusRate] = useState(
    defaultValues.firstTopupBonusRate
  )
  const [saving, setSaving] = useState(false)
  const [rewards, setRewards] = useState<Array<Record<string, any>>>([])
  const [confirming, setConfirming] = useState<string | null>(null)

  async function loadRewards() {
    const res = await api.get('/api/user/referral?p=1&page_size=100')
    setRewards(res.data?.data?.items ?? [])
  }

  useEffect(() => { loadRewards().catch(() => undefined) }, [])

  async function confirmReward(tradeNo: string) {
    setConfirming(tradeNo)
    try {
      await api.post('/api/user/referral/confirm', { trade_no: tradeNo })
      await loadRewards()
      toast.success(t('Referral reward confirmed'))
    } finally {
      setConfirming(null)
    }
  }

  const initialTiers = JSON.stringify(parseTiers(defaultValues.tiers))
  const isDirty =
    enabled !== defaultValues.enabled ||
    autoGrant !== defaultValues.autoGrant ||
    JSON.stringify(tiers) !== initialTiers ||
    firstTopupBonusRate !== defaultValues.firstTopupBonusRate

  function updateTier(index: number, patch: Partial<ReferralTier>) {
    setTiers((prev) =>
      prev.map((tier, i) => (i === index ? { ...tier, ...patch } : tier))
    )
  }

  function addTier() {
    setTiers((prev) => [...prev, { min_volume: 0, rate: 0 }])
  }

  function removeTier(index: number) {
    setTiers((prev) => prev.filter((_, i) => i !== index))
  }

  async function onSave() {
    for (const tier of tiers) {
      if (
        !Number.isFinite(tier.min_volume) ||
        !Number.isFinite(tier.rate) ||
        tier.min_volume < 0 ||
        tier.rate < 0 ||
        tier.rate > 100
      ) {
        toast.error(
          t('Tier values invalid: min must be ≥ 0 and rate between 0 and 100')
        )
        return
      }
    }

    if (new Set(tiers.map((tier) => tier.min_volume)).size !== tiers.length) {
      toast.error(t('Tier minimum performance values must be unique'))
      return
    }
    if (
      !Number.isFinite(firstTopupBonusRate) ||
      firstTopupBonusRate < 0 ||
      firstTopupBonusRate > 100
    ) {
      toast.error(t('First top-up bonus rate must be between 0 and 100'))
      return
    }

    const sorted = [...tiers].sort((a, b) => a.min_volume - b.min_volume)
    const updates: Array<{ key: string; value: string }> = []

    if (enabled !== defaultValues.enabled) {
      updates.push({ key: 'referral_setting.enabled', value: String(enabled) })
    }
    if (autoGrant !== defaultValues.autoGrant) {
      updates.push({ key: 'referral_setting.auto_grant', value: String(autoGrant) })
    }
    const serialized = JSON.stringify(sorted)
    if (serialized !== initialTiers) {
      updates.push({ key: 'referral_setting.tiers', value: serialized })
    }
    if (firstTopupBonusRate !== defaultValues.firstTopupBonusRate) {
      updates.push({
        key: 'referral_setting.first_topup_bonus_rate',
        value: String(firstTopupBonusRate),
      })
    }

    if (updates.length === 0) {
      toast.info(t('No changes to save'))
      return
    }

    setSaving(true)
    try {
      for (const update of updates) {
        await updateOption.mutateAsync(update)
      }
      setTiers(sorted)
      toast.success(t('Settings saved'))
    } finally {
      setSaving(false)
    }
  }

  const busy = saving || updateOption.isPending

  return (
    <SettingsSection
      title={t('Referral Commission')}
      description={t(
        'Reward inviters with commission points when invited users recharge'
      )}
    >
      <div className='space-y-6'>
        <div className='flex flex-row items-center justify-between rounded-lg border border-border/60 bg-card p-4 transition-colors hover:bg-accent/20'>
          <div className='space-y-0.5'>
            <Label className='text-base'>
              {t('Enable referral commission')}
            </Label>
            <p className='text-sm text-muted-foreground'>
              {t(
                "When enabled, an inviter's tier is based on the cumulative recharge performance of all their invitees"
              )}
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} disabled={busy} />
        </div>

        <div className='space-y-3'>
          <Label className='text-base'>{t('Referral reward records')}</Label>
          <div className='overflow-x-auto rounded-lg border border-border/60'>
            <table className='w-full text-sm'>
              <thead><tr className='border-b text-left text-xs text-muted-foreground'>
                <th className='p-2'>{t('Trade No.')}</th><th className='p-2'>{t('User ID')}</th>
                <th className='p-2'>{t('Quota')}</th><th className='p-2'>{t('Status')}</th><th className='p-2'>{t('Action')}</th>
              </tr></thead>
              <tbody>{rewards.map((row) => (
                <tr key={row.trade_no} className='border-b last:border-0'>
                  <td className='p-2 font-mono text-xs'>{row.trade_no}</td><td className='p-2'>{row.user_id}</td>
                  <td className='p-2 tabular-nums'>{row.referral_base_quota}</td>
                  <td className='p-2'>{row.referral_commission_granted ? t('Granted') : t('Pending')}</td>
                  <td className='p-2'>{!row.referral_commission_granted && <Button size='sm' variant='outline' onClick={() => confirmReward(row.trade_no)} disabled={confirming === row.trade_no}><CheckCircle className='mr-1 size-3' />{t('Confirm reward')}</Button>}</td>
                </tr>
              ))}</tbody>
            </table>
            {rewards.length === 0 && <p className='p-3 text-sm text-muted-foreground'>{t('No referral reward records')}</p>}
          </div>
        </div>

        <div className='flex flex-row items-center justify-between rounded-lg border border-border/60 bg-card p-4 transition-colors hover:bg-accent/20'>
          <div className='space-y-0.5'>
            <Label className='text-base'>{t('Automatically grant referral rewards')}</Label>
            <p className='text-sm text-muted-foreground'>
              {t('When disabled, successful referral rewards remain pending until an administrator confirms them.')}
            </p>
          </div>
          <Switch checked={autoGrant} onCheckedChange={setAutoGrant} disabled={busy} />
        </div>

        {enabled && (
          <div className='space-y-4'>
            <div>
              <Label className='text-base'>{t('Commission Tiers')}</Label>
              <p className='text-sm text-muted-foreground'>
                {t(
                  "The inviter's current tier applies to every recharge from all invitees. Higher cumulative performance raises the maintenance commission rate."
                )}
              </p>
            </div>

            <div className='space-y-3'>
              {tiers.length === 0 && (
                <p className='text-sm text-muted-foreground'>
                  {t('No tiers configured')}
                </p>
              )}
              {tiers.map((tier, index) => (
                <div
                  key={index}
                  className='flex items-end gap-3 rounded-lg border border-border/60 bg-card p-3'
                >
                  <div className='flex-1 space-y-1'>
                    <Label className='text-xs text-muted-foreground'>
                      {t('Minimum cumulative performance (USD)')}
                    </Label>
                    <Input
                      type='number'
                      min={0}
                      step='0.01'
                      value={tier.min_volume}
                      disabled={busy}
                      onChange={(e) =>
                        updateTier(index, {
                          min_volume: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className='flex-1 space-y-1'>
                    <Label className='text-xs text-muted-foreground'>
                      {t('Commission rate (%)')}
                    </Label>
                    <Input
                      type='number'
                      min={0}
                      max={100}
                      step='0.1'
                      value={tier.rate}
                      disabled={busy}
                      onChange={(e) =>
                        updateTier(index, { rate: Number(e.target.value) })
                      }
                    />
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    disabled={busy}
                    onClick={() => removeTier(index)}
                    aria-label={t('Remove tier')}
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={busy}
              onClick={addTier}
            >
              <Plus className='mr-1 h-4 w-4' />
              {t('Add tier')}
            </Button>

            <div className='space-y-2 rounded-lg border border-border/60 bg-card p-4'>
              <Label htmlFor='first-topup-bonus-rate' className='text-base'>
                {t('First top-up bonus rate (%)')}
              </Label>
              <p className='text-sm text-muted-foreground'>
                {t(
                  "Added once, on top of the inviter's maintenance commission, when each invitee completes their first successful recharge."
                )}
              </p>
              <Input
                id='first-topup-bonus-rate'
                type='number'
                min={0}
                max={100}
                step='0.1'
                value={firstTopupBonusRate}
                disabled={busy}
                onChange={(event) =>
                  setFirstTopupBonusRate(Number(event.target.value))
                }
              />
            </div>
          </div>
        )}

        <Button type='button' disabled={!isDirty || busy} onClick={onSave}>
          {busy ? t('Saving...') : t('Save referral settings')}
        </Button>
      </div>
    </SettingsSection>
  )
}
